import httpClient from './httpClient';

/**
 * DGII electronic billing API client (httpClient-based).
 *
 * Mirrors the protected `/api/dgii` routes (`src/server/routes/dgii.js` +
 * `src/server/controllers/dgiiController.js`):
 * - config (FEC-1):        GET/PUT /api/dgii/config
 * - certificate (FEC-3):   POST /api/dgii/certificado (multipart, .p12/.pfx only)
 * - NCF sequences (FEC-7): GET/POST/PUT/DELETE /api/dgii/secuencias[/:id]
 * - issued e-CFs (FEC-7):  GET /api/dgii/comprobantes
 * - status (CON-1 interim): GET /api/dgii/comprobantes/:encf
 * - bulk verify (CON-2 interim): POST /api/dgii/verificar
 */

/** DGII environment, mirroring `ConfiguracionDGII.ambiente` (model enum). */
export type AmbienteDGII = 'testecf' | 'certecf' | 'ecf';

export const AMBIENTES_DGII: readonly AmbienteDGII[] = ['testecf', 'certecf', 'ecf'];

export interface CertificadoDGII {
  activo: boolean;
  titular?: string;
  fechaVencimiento?: string;
}

export interface ConfiguracionDGII {
  tenantId: string;
  rncEmisor?: string;
  razonSocial?: string;
  ambiente?: AmbienteDGII;
  certificado?: CertificadoDGII;
}

export interface SecuenciaNCF {
  _id?: string;
  tenantId: string;
  tipoComprobante: string;
  rangoDesde: number;
  rangoHasta: number;
  secuenciaActual: number;
  fechaVencimiento: string;
  activo: boolean;
  ambiente: AmbienteDGII;
}

/** Issued e-CF ledger entry (monitor list, FEC-7). */
export interface ComprobanteEmitido {
  encf: string;
  tipoComprobante: string;
  estado: string;
  trackId?: string | null;
  fechaEmision: string;
  ambiente: AmbienteDGII;
  montoTotal: number;
  facturaId?: string;
}

/** Single e-CF status consult response (CON-1). */
export interface EstadoComprobanteDGII {
  encf: string;
  estadoDGII: string;
  codigoEstado?: number | null;
  trackId?: string | null;
  fechaEmision: string;
  ambiente: AmbienteDGII;
  // CON-4 (slice 5b): consulta timbre QR payload fields — the Consulta DGII
  // page renders the DGII verification QR from these ledger fields.
  rncEmisor?: string | null;
  rncComprador?: string | null;
  montoTotal?: number | null;
  codigoSeguridad?: string | null;
  fechaFirma?: string | null;
  tipoComprobante?: string | null;
  esResumenFC?: boolean;
}

/** CON-3: resend response (the e-CF is re-sent to the DGII). */
export interface ResultadoReenvio {
  exito: boolean;
  mensaje?: string;
  ecf?: { encf?: string; estado?: string; trackId?: string | null };
}

/**
 * Commercial approval (APR-2), mirroring `AprobacionComercial`: the
 * aprobacioncomercial callback payload stored for the tenant, read-only on
 * this page. `estadoDGII` is a STRING on purpose (DGII codes can have leading
 * zeros, e.g. '00').
 */
export interface AprobacionComercial {
  _id: string;
  encf: string;
  rncEmisor: string;
  rncComprador: string;
  montoTotal: number;
  fechaEmision: string;
  fechaHoraAprobacion: string;
  estadoDGII?: string | null;
  decisionEmisor: 'Aprobado' | 'Rechazado' | 'Pendiente';
  motivoRechazo?: string;
  direccion?: 'recibida' | 'emitida';
  ambiente: AmbienteDGII;
  createdAt?: string;
}

/** APR-2: lists the tenant's stored commercial approvals, most recent first. */
export const obtenerAprobacionesComerciales = async (): Promise<AprobacionComercial[]> => {
  const response = await httpClient.get('/api/dgii/aprobaciones');
  return response.data;
};

/**
 * Stored client (CLI-1/CLI-3), mirroring `Cliente`: the source of truth for
 * the buyer identity on electronic crédito fiscal (e31) invoices.
 */
export interface ClienteDGII {
  _id: string;
  nombre: string;
  documentoIdentidad?: string;
  rnc?: string;
  telefono?: string;
  email?: string;
  direccion?: string;
  activo?: boolean;
}

/** CLI-1: lists the tenant's stored clients (sorted by name). */
export const obtenerClientesDGII = async (): Promise<ClienteDGII[]> => {
  const response = await httpClient.get('/api/dgii/clientes');
  return response.data;
};

/** CLI-1/CLI-2: creates a stored client (RNC validated server-side with the same DGII checksum). */
export const crearClienteDGII = async (datos: Partial<ClienteDGII>): Promise<ClienteDGII> => {
  const response = await httpClient.post('/api/dgii/clientes', datos);
  return response.data;
};

/** CLI-1/CLI-2: updates a stored client (whitelisted fields only, RNC re-validated). */
export const actualizarClienteDGII = async (
  id: string,
  datos: Partial<ClienteDGII>
): Promise<ClienteDGII> => {
  const response = await httpClient.put(`/api/dgii/clientes/${encodeURIComponent(id)}`, datos);
  return response.data;
};

/**
 * CLI-1: deletes a stored client. The backend REFUSES (409) while the client
 * is referenced by an invoice — the page surfaces that message as-is.
 */
export const eliminarClienteDGII = async (id: string): Promise<void> => {
  await httpClient.delete(`/api/dgii/clientes/${encodeURIComponent(id)}`);
};

export interface ResultadoVerificacion {
  encf: string;
  estado: string;
  trackId?: string | null;
  error?: string;
  /** Ambiente the result was resolved from (FEC-8: SIMULATED results are provably certification-scoped). */
  ambiente: AmbienteDGII;
}

/** FEC-1: reads the current tenant's DGII configuration (or null). */
export const obtenerConfiguracionDGII = async (): Promise<ConfiguracionDGII | null> => {
  const response = await httpClient.get('/api/dgii/config');
  return response.data ?? null;
};

/** FEC-1: creates/updates the current tenant's DGII configuration. */
export const guardarConfiguracionDGII = async (
  data: Partial<ConfiguracionDGII>
): Promise<ConfiguracionDGII> => {
  const response = await httpClient.put('/api/dgii/config', data);
  return response.data;
};

/** FEC-3: uploads a .p12/.pfx certificate with its password (multipart). */
export const subirCertificadoDGII = async (
  archivo: File,
  password: string
): Promise<{ exito: boolean; mensaje: string }> => {
  const formData = new FormData();
  formData.append('certificado', archivo);
  formData.append('password', password);
  // Content-Type explícito multipart: el default global del httpClient es
  // application/json y un header vacío NO lo reemplaza — sin esto axios
  // serializa el FormData como JSON y multer nunca recibe el archivo
  // (mismo patrón que configuracionService/maintenanceService).
  const response = await httpClient.post('/api/dgii/certificado', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/** FEC-7: lists the tenant's NCF sequence ranges. */
export const obtenerSecuenciasNCF = async (): Promise<SecuenciaNCF[]> => {
  const response = await httpClient.get('/api/dgii/secuencias');
  return response.data;
};

/**
 * FEC-7: updates an NCF sequence range (whitelisted fields only — the backend
 * REFUSES to move the bounds of a range that already issued e-NCF numbers).
 */
export const actualizarSecuenciaDGII = async (
  id: string,
  datos: Partial<SecuenciaNCF>
): Promise<SecuenciaNCF> => {
  const response = await httpClient.put(`/api/dgii/secuencias/${encodeURIComponent(id)}`, datos);
  return response.data;
};

/**
 * FEC-7: deletes an NCF sequence range. The backend REFUSES (400) while the
 * range has issued at least one e-NCF — the page surfaces that message as-is.
 */
export const eliminarSecuenciaDGII = async (id: string): Promise<void> => {
  await httpClient.delete(`/api/dgii/secuencias/${encodeURIComponent(id)}`);
};

/** FEC-7: lists the tenant's issued e-CFs, most recent first. */
export const obtenerComprobantesEmitidos = async (): Promise<ComprobanteEmitido[]> => {
  const response = await httpClient.get('/api/dgii/comprobantes');
  return response.data;
};

/**
 * CON-1 (interim): queries the ledger state of one issued e-CF by e-NCF,
 * scoped to an ambiente (FEC-8: the backend rejects a missing/invalid
 * ambiente with a 400, so the consult can never cross environments).
 */
export const obtenerEstadoComprobante = async (
  encf: string,
  ambiente: AmbienteDGII
): Promise<EstadoComprobanteDGII> => {
  const response = await httpClient.get(
    `/api/dgii/comprobantes/${encodeURIComponent(encf)}?ambiente=${encodeURIComponent(ambiente)}`
  );
  return response.data;
};

/**
 * CON-2 (interim): bulk-verifies e-CFs against the ledger within ONE ambiente,
 * never stopping on failure (FEC-8: results carry the ambiente they came from).
 */
export const verificarComprobantesDGII = async (
  comprobantes: string[],
  ambiente: AmbienteDGII
): Promise<{ verificados: ResultadoVerificacion[] }> => {
  const response = await httpClient.post('/api/dgii/verificar', {
    ambiente,
    comprobantes: comprobantes.map((encf) => ({ encf })),
  });
  return response.data;
};

/**
 * CON-3: resends an e-CF in Pendiente state, scoped to the ambiente the e-CF
 * was consulted in (JD R1: the backend now validates the ambiente fail-closed
 * — same CON-1/CON-2 contract — so a resend can never cross the
 * certification/production boundary, and rejects non-Pendiente e-CFs with a
 * 400 (CON-3 "only from Pendiente")).
 */
export const reenviarComprobanteDGII = async (encf: string, ambiente: AmbienteDGII): Promise<ResultadoReenvio> => {
  const response = await httpClient.post('/api/dgii/reenviar', { encf, ambiente });
  return response.data;
};

/**
 * Received comprobante (REC-1), mirroring `ComprobanteRecibido`:
 * `tipoComprobante` is DERIVED server-side from the number — the client never
 * sends it; `ambiente` comes from the tenant config, never from the client.
 */
export interface ComprobanteRecibido {
  _id: string;
  encf: string;
  tipoComprobante: string;
  rncEmisor: string;
  razonSocialEmisor?: string;
  rncComprador?: string;
  razonSocialComprador?: string;
  montoTotal: number;
  montoGravado?: number;
  montoExento?: number;
  totalItbis?: number;
  totalDescuento?: number;
  fechaEmision: string;
  fechaFirma?: string;
  fechaRecepcion?: string;
  estadoAcuse?: string;
  ambiente: AmbienteDGII;
  origenRegistro?: 'webhook' | 'manual' | 'qr' | 'sync-central';
  notas?: string;
}

/** Input for REC-1 manual/QR-sourced registration (no _id, no tipoComprobante). */
export type DatosRecibidoManual = Omit<ComprobanteRecibido, '_id' | 'tipoComprobante' | 'ambiente'> & {
  origenRegistro?: 'manual' | 'qr';
};

/** REC-1: lists the tenant's received comprobantes, most recent first. */
export const obtenerRecibidos = async (): Promise<ComprobanteRecibido[]> => {
  const response = await httpClient.get('/api/dgii/recibidos');
  return response.data;
};

/** REC-1: registers a received comprobante manually (or from a QR scan). */
export const crearRecibidoManual = async (
  datos: DatosRecibidoManual
): Promise<ComprobanteRecibido> => {
  const response = await httpClient.post('/api/dgii/recibidos', datos);
  return response.data;
};

/** REC-1: edits whitelisted fields of a received comprobante. */
export const actualizarRecibidoManual = async (
  id: string,
  datos: Partial<ComprobanteRecibido>
): Promise<ComprobanteRecibido> => {
  const response = await httpClient.put(`/api/dgii/recibidos/${encodeURIComponent(id)}`, datos);
  return response.data;
};

/** REC-1: deletes a received comprobante (tenant-scoped). */
export const eliminarRecibidoManual = async (id: string): Promise<void> => {
  await httpClient.delete(`/api/dgii/recibidos/${encodeURIComponent(id)}`);
};

/**
 * Counts envelope of a successful `POST /api/proxy/recibidos/sync` pull
 * (RCS-7/RCS-8).
 */
export interface ResultadoSyncRecibidos {
  ok: boolean;
  procesados: number;
  creados: number;
  actualizados: number;
  omitidos: number;
  paginas: number;
  masDisponible: boolean;
  motivos: Record<string, number>;
}

/** 200 guard answer of the sync endpoint: NOT a pull (sync_disabled/tenant_mismatch). */
export interface SyncRecibidosOmitido {
  skipped: true;
  motivo: string;
}

/**
 * RCS-7/RCS-8: best-effort trigger for the central→local pull of received
 * comprobantes. Clone of `syncResultado` (resultadosService.ts) minus payload
 * building — the server reads the tenant from the request context, so the
 * POST carries no body. Env-gated by `VITE_SYNC_RECIBIDOS_URL` (kill switch:
 * unset ⇒ NO HTTP call, null). Never rethrows: any failure is swallowed with
 * a console.warn and resolved as null so the page can never crash.
 */
export const sincronizarRecibidosConCentral = async (): Promise<
  ResultadoSyncRecibidos | SyncRecibidosOmitido | null
> => {
  try {
    const syncUrl = import.meta.env.VITE_SYNC_RECIBIDOS_URL;
    if (!syncUrl) {
      console.warn('Recibidos sync disabled: VITE_SYNC_RECIBIDOS_URL is not defined');
      return null;
    }

    // URL must be relative (starts with '/') or absolute (starts with 'http').
    if (!syncUrl.startsWith('http') && !syncUrl.startsWith('/')) {
      console.warn('Recibidos sync disabled: invalid sync URL');
      return null;
    }

    const response = await httpClient.post(syncUrl);
    return response.data as ResultadoSyncRecibidos | SyncRecibidosOmitido;
  } catch (error) {
    // Best-effort (RCS-8): never rethrow — the caller shows a toast and moves on.
    console.warn(
      'Recibidos sync failed (this is normal if the central is not configured):',
      error instanceof Error ? error.message : 'Unknown error'
    );
    return null;
  }
};

/**
 * NCD-1: credit (e34) / debit (e33) note row, mirroring the projected fields
 * of the /api/dgii/notas route (numeroNotaCredito/Debito + encf + estadoDGII).
 */
export interface NotaCreditoDebito {
  _id: string;
  numeroNotaCredito?: string;
  numeroNotaDebito?: string;
  numeroFacturaReferencia: string;
  cliente: string;
  clienteRNC?: string;
  total: number;
  estado: string;
  tipoComprobante: string;
  ambiente: AmbienteDGII | null;
  encf?: string | null;
  estadoDGII?: string | null;
  trackId?: string | null;
  createdAt?: string;
}

/** NCD-1: lists the tenant's credit and debit notes, most recent first. */
export const obtenerNotasCreditoDebito = async (): Promise<{
  creditos: NotaCreditoDebito[];
  debitos: NotaCreditoDebito[];
}> => {
  const response = await httpClient.get('/api/dgii/notas');
  return response.data;
};

/**
 * RPT-5: generated DGII monthly report (606/607/608) in TXT or XLSX —
 * `archivo` is the TXT content (string) or the XLSX as base64 (reference
 * response contract, see `reportesDGIIControlador.js`).
 */
export interface ReporteDGII {
  exito: boolean;
  archivo: string;
  nombreArchivo: string;
  formato: 'txt' | 'xlsx';
  cantidadRegistros: number;
  totales?: {
    totalMontoFacturado: number;
    totalITBIS: number;
  };
}

/** RPT-5: generates a DGII monthly report for the tenant. */
export const generarReporteDGII = async (
  tipo: '606' | '607' | '608',
  mes: number,
  anio: number,
  formato: 'txt' | 'xlsx'
): Promise<ReporteDGII> => {
  const response = await httpClient.get('/api/dgii/reportes/' + tipo, {
    params: { mes, anio, formato },
  });
  return response.data;
};

/** RPT-5: downloads a generated report to the user's disk (browser-side). */
export function descargarReporteDGII(reporte: ReporteDGII) {
  const esXlsx = reporte.formato === 'xlsx';
  const blob = esXlsx
    ? new Blob([Uint8Array.from(atob(reporte.archivo), (c) => c.charCodeAt(0))], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
    : new Blob([reporte.archivo], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = reporte.nombreArchivo;
  document.body.appendChild(enlace);
  enlace.click();
  document.body.removeChild(enlace);
  URL.revokeObjectURL(url);
}